<template>
  <div class="max-w-2xl space-y-4">
      <div class="flex items-center gap-4 relative">
        <div class="relative">
          <div
            v-if="isLoading"
            class="absolute z-20 inset-0 flex items-center justify-center bg-gray-100/70 rounded-full backdrop-blur-[1px]"
          >
            <Loader2 class="w-6 h-6 animate-spin text-black" />
          </div>
          <img
            v-if="profileImage"
            :src="profileImage"
            alt="Avatar"
            class="w-28 h-28 rounded-full object-cover border"
          />
          <div
            v-else
            class="w-28 h-28 rounded-full bg-gray-100 flex items-center justify-center border"
          >
            <UserRound class="w-10 h-10 text-gray-400" />
          </div>
        </div>
        <div class="flex flex-col gap-2">
          <!-- Only show upload button for non-guide users -->
          <template v-if="!isGuideMode">
            <Input
              id="avatar-upload"
              type="file"
              class="hidden"
              @change="handleFileSelection"
              accept="image/png, image/jpeg, image/gif"
              :key="fileInputKey"
            />
            <Button
              as="label"
              for="avatar-upload"
              variant="outline"
              size="sm"
              :disabled="isLoading"
              class="cursor-pointer"
            >
              Change Picture
            </Button>
          </template>
          <Button
            v-if="profileImage"
            size="sm"
            variant="destructive"
            class="bg-white text-red-500 border-red-500 border-[1px] hover:bg-red-500 hover:text-white"
            @click="removeAvatar"
            :disabled="isLoading"
          >
            Remove Picture
          </Button>
        </div>
      </div>
      <PhotoCropperModal
        v-model:is-open="isCropperOpen"
        :image-url="selectedImageUrl"
        @crop="handleCroppedImage"
      />
  </div>
</template>

<script setup lang="ts">
import { ref, computed, onMounted } from 'vue'
import { Loader2, UserRound } from 'lucide-vue-next'
import { useUserStore } from '@/stores/user'
import { toast } from '@/components/ui/toast/use-toast'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import api from '@/utils/api'
import { API_ROUTES } from '@/const/apiRoutes'
import PhotoCropperModal from './PhotoCropperModal.vue'

const props = defineProps<{
isGuideMode?: boolean
profileId?: number
photoId?: number
initialImage?: string
}>()

const userStore = useUserStore()
const fileInputKey = ref(0)
const isLoading = ref(false)
const isCropperOpen = ref(false)
const selectedImageUrl = ref('')

const profileImage = computed(() => {
if (props.isGuideMode) {
  return props.initialImage
}
return userStore.currentUser?.avatar_image
})

onMounted(async () => {
if (!props.isGuideMode && !userStore.currentUser) {
  await userStore.fetchCurrentUser()
}
})

const handleFileSelection = (event: Event) => {
const target = event.target as HTMLInputElement
const file = target.files?.[0]
if (file) {
  selectedImageUrl.value = URL.createObjectURL(file)
  isCropperOpen.value = true
  fileInputKey.value++ // Reset file input
}
}

const handleCroppedImage = async (base64String: string) => {
isLoading.value = true
try {
  await userStore.updateAvatar({ avatar_image: base64String })
  await userStore.fetchCurrentUser()
  toast({ title: 'Success', description: 'Avatar updated successfully!' })
} catch (error: any) {
  console.error('Error updating avatar:', error)
  const errorMessage =
    error.response?.data?.message || error.message || 'Failed to update avatar.'
  toast({ title: 'Error', description: errorMessage, variant: 'destructive' })
} finally {
  isLoading.value = false
  // Clean up object URL
  if (selectedImageUrl.value) {
    URL.revokeObjectURL(selectedImageUrl.value)
    selectedImageUrl.value = ''
  }
}
}

const removeAvatar = async () => {
isLoading.value = true
try {
  if (props.isGuideMode && props.photoId) {
    // Use the guide-specific endpoint for photo deletion
    await api.delete(API_ROUTES.DELETE_PROFILE_PHOTO(props.photoId.toString()))
  } else {
    await userStore.deleteAvatar()
    await userStore.fetchCurrentUser()
  }
  toast({ title: 'Success', description: 'Avatar removed successfully!' })
} catch (error: any) {
  console.error('Error removing avatar:', error)
  const errorMessage =
    error.response?.data?.message || error.message || 'Failed to remove avatar.'
  toast({ title: 'Error', description: errorMessage, variant: 'destructive' })
} finally {
  isLoading.value = false
}
}
</script>

<style scoped></style>